import React, {useState} from "react";
import GrayModal from "../../../dataDisplay/GrayModal";
import {noop} from "../../../commons/misc";
import {Row} from "reactstrap";
import styles from "./NewPasswordModal.module.scss"
import {Environment} from "relay-runtime";
import {ModalErrorAlert} from "../../../commons/errors";
import ChangePasswordWithTokenMutation from "../../../mutations/ChangePasswordWithTokenMutation";
import {useLocation} from "react-router-dom";
import {DefaultInput} from '../../../atoms/Input';
import Tooltip from "../../../atoms/Tooltip";

const passwordTip = <>
  Please add a strong password by<br/>
  making sure it consists of at least:<br/>
  <br/>
  • 8 characters<br/>
  • 1 number<br/>
  • 1 special character<br/>
  <br/>
  e.g. jWa8katy9A=4
</>;

const PASSWORDS_DOES_NOT_MATCH = "The password typed in the two fields does not match.";
const TOKEN_MISSING = "Invalid password reset request. Are you sure you got this link via a valid Vuuh email?"

const ERROR_DICTIONARY = {
  "users.invalid_reset_password_token": "This reset password link is invalid",
  "users.password_too_short": "The password is too short. It should be at least 8 characters long",
  "users.password_not_strong_enough": "The new password is not strong enough.",
}

const translator = (error: string | null) => {
  if (ERROR_DICTIONARY[error as keyof typeof ERROR_DICTIONARY]) {
    return ERROR_DICTIONARY[error as keyof typeof ERROR_DICTIONARY]
  } else if (error === PASSWORDS_DOES_NOT_MATCH) {
    return PASSWORDS_DOES_NOT_MATCH
  } else if (error === TOKEN_MISSING) {
    return TOKEN_MISSING
  } else {
    return "Something went wrong"
  }
}

const getIsInputInvalid = (error: string | Error) => {
  const errorString = typeof error === "string" ? error : error.message;
  if (!errorString) {
    return false;
  }

  return errorString === "users.password_too_short" || errorString === "users.password_not_strong_enough" || errorString === PASSWORDS_DOES_NOT_MATCH;
}

type TriggerPasswordResetModalProps = {
  environment: Environment
  scope: string
  onSend: () => void
}

export default function NewPasswordModal({environment, onSend}: TriggerPasswordResetModalProps) {
  let [newPassword, setNewPassword] = useState('')
  let [confirmPassword, setConfirmPassword] = useState('')
  let [error, setError] = useState<string | Error>('')

  const location = useLocation();
  const isInputInvalid = getIsInputInvalid(error);
  let token = new URLSearchParams(location.search).get("token")

  const onSubmit = () => {
    if (newPassword === confirmPassword && token) {

      ChangePasswordWithTokenMutation(
        environment,
        {password: newPassword, token: token},
        () => {
          onSend()
        },
        (error) => {
          setError(error[0].message);
        }
      )
    } else if (token && newPassword !== confirmPassword) {
      setError(PASSWORDS_DOES_NOT_MATCH)
    } else {
      setError(TOKEN_MISSING)
    }
  }

  const onPressEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && newPassword)
      onSubmit()
  }

  return <GrayModal
    isOpen={true}
    toggle={noop}
    style={{minWidth: "34rem"}}
    title={"New password"}
    primaryHeaderButton={{text: "Continue", onClick: onSubmit, isDisabled: !newPassword || !confirmPassword}}
    bodyContent={
      <div className={"my-4 mx-5"}>
        <Row className={"mb-3"} noGutters>
          <DefaultInput
            type={"password"}
            autoComplete={"new-password"}
            className={styles.passwordInput}
            onChange={(e) => setNewPassword(e.target.value)}
            value={newPassword}
            onKeyDown={onPressEnter}
            invalid={isInputInvalid}
            placeholder={"Enter new password"}
            autoFocus/>
          <Tooltip text={passwordTip}
                   wrapperStyle={{marginLeft: '0.75rem', marginTop: '0.5rem'}}
                   mode={'help'}
                   arrow={false}
                   placement={"bottomRight"}>
            <i className={`fa-light fa-circle-question ${styles.tooltipIcon}`}/>
          </Tooltip>
        </Row>
        <Row noGutters>
          <DefaultInput
            type={"password"}
            autoComplete={"new-password"}
            onKeyDown={onPressEnter}
            className={styles.passwordInput}
            onChange={(e) => setConfirmPassword(e.target.value)}
            value={confirmPassword}
            invalid={isInputInvalid}
            placeholder={"Re-enter new password"}/>
        </Row>
        {error && <Row className={"mt-3"} noGutters>
          <ModalErrorAlert error={error} translator={translator}/>
        </Row>}
      </div>
    }/>
}
